import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Card, CardContent } from "../../../components/ui/card";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Loader, Plus, Trash2, X } from "lucide-react";
import {
  formatDate,
  formatToShortDate,
  formatUniversalNumber,
} from "../../../lib/utils";
import { useContext, useEffect, useRef, useState } from "react";
import { BankingContext } from "../../../providers/BankingProvider";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  ApplyAndConfirmTransactionDataPayload,
  getTransactionEdit,
} from "./BankingApis";
import CustomReactSelect from "../../../components/ui/custom-react-select";
import { UIContext } from "../../../providers/UIProvider";
import { Delete } from "../../../assets";

interface EditTransactionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedRows: any[];
  isEditTransaction: string;
  setIsEditTransaction: (isEditTransaction: string) => void;
}

// Fixed types for react-select values
type SelectOption = {
  value: string;
  label: string;
  other?: string; // For customer/vendor distinction
};

type SplitRow = {
  customerName: SelectOption | null;
  category: SelectOption | null;
  amount: string;
  description: string;
};

type EditTransactionFormValues = {
  transactionType: SelectOption | null;
  customerName: SelectOption | null;
  category: SelectOption | null;
  remark: string;
  splitRows: SplitRow[];
};

export const EditTransactionDialog = ({
  open,
  onOpenChange,
  selectedRows,
  isEditTransaction,
  setIsEditTransaction,
}: EditTransactionDialogProps) => {
  const {
    loading,
    transactionTypeOptions,
    categoryOptions,
    customerVendorOptions,
    handleApply,
    editTransaction,
  } = useContext(BankingContext);

  const isConfirming = loading?.isConfirming ?? false;
  const [showSplitTransaction, setShowSplitTransaction] = useState(false);
  const { businessId, onSuccess, onError } = useContext(UIContext);

  const defaultFormValues: EditTransactionFormValues = {
    transactionType: null,
    customerName: null,
    category: null,
    remark: "",
    splitRows: [],
  };

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    setError,
    clearErrors,
    setFocus,
    formState: { errors },
  } = useForm<EditTransactionFormValues>({
    defaultValues: defaultFormValues,
  });

  // Changed: Use useRef to store refs for each split row
  const customerRefs = useRef<{ [key: number]: any }>({});
  const [isFetchingEditTransaction, setIsFetchingEditTransaction] =
    useState(false);
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "splitRows",
  });

  // Clear all values when modal closes
  useEffect(() => {
    if (!open) {
      reset(defaultFormValues);
      setShowSplitTransaction(false);
      clearErrors();
      // Clear refs when modal closes
      customerRefs.current = {};
    }
  }, [open, reset, clearErrors]);

  const fetchEditTransaction = async () => {
    setIsFetchingEditTransaction(true);
    try {
      const transaction = await getTransactionEdit(
        businessId,
        isEditTransaction
      );

      onSuccess?.("GET", "PlaidTransaction", "Data retrieved successfully!");

      // Set transaction type
      const matchingType = transactionTypeOptions?.find(
        (item) => item.value === transaction.transactionEntityTypeId?.toString()
      );
      if (matchingType) {
        setValue("transactionType", matchingType);
      }

      // Set remarks
      setValue("remark", transaction.remarks || "");

      if (transaction.transactionDetailsCategoryModels?.length > 0) {
        if (transaction.transactionDetailsCategoryModels?.length > 1) {
          setShowSplitTransaction(true);
          // Create split rows data with proper types
          const splitRowsData: SplitRow[] =
            transaction.transactionDetailsCategoryModels.map((item) => {
              const customer =
                customerVendorOptions?.find(
                  (cus) => cus.value === item.customerId
                ) || null;
              const category =
                categoryOptions
                  ?.flatMap((group) => group?.options) // Flatten all options
                  .find((opt) => opt.value === item.categoryId) || null;

              return {
                customerName: customer,
                category: category,
                amount: item.amount?.toString() || "",
                description: item.description || "",
              };
            });

          // Replace all split rows
          replace(splitRowsData);
        } else {
          // For non-split transaction, set the first item's data
          const firstItem = transaction.transactionDetailsCategoryModels[0];
          const customer =
            customerVendorOptions?.find(
              (cus) => cus.value === firstItem.customerId
            ) || null;
          const category =
            categoryOptions
              ?.flatMap((group) => group?.options) // Flatten all options
              .find((opt) => opt.value === firstItem.categoryId) || null;

          setValue("customerName", customer);
          setValue("category", category);
        }
      }
      setIsFetchingEditTransaction(false);
    } catch (error) {
      setIsFetchingEditTransaction(false);
      console.error("Error fetching transaction:", error);
    }
  };

  useEffect(() => {
    if (
      businessId &&
      isEditTransaction &&
      transactionTypeOptions &&
      customerVendorOptions &&
      categoryOptions
    ) {
      fetchEditTransaction();
    }
  }, [
    isEditTransaction,
    transactionTypeOptions,
    customerVendorOptions,
    categoryOptions,
  ]);

  // Watch values for calculations
  const splitRows = watch("splitRows");
  const splitAmount =
    splitRows?.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0) ||
    0;
  const originalAmount = editTransaction?.amount || 0;
  const difference = Math.abs(originalAmount) - splitAmount;

  // Create default split row
  const createDefaultSplitRow = (): SplitRow => ({
    customerName: null,
    category: null,
    amount: "",
    description: "",
  });

  // Prefill split row when Split is clicked
  const handleSplit = () => {
    if (fields.length === 0) {
      // Add initial split row
      append(createDefaultSplitRow());
    }
    setShowSplitTransaction(true);

    // Focus on the first row's customer field after split is enabled
    setTimeout(() => {
      if (customerRefs.current[0]) {
        customerRefs.current[0].focus?.();
      }
    }, 0);
  };

  // To close and reset split section
  const handleCloseSplit = () => {
    setShowSplitTransaction(false);
    replace([]); // Clear all split rows
    // Clear refs when closing split
    customerRefs.current = {};
  };

  // Function to clear all form data
  const clearAllFormData = () => {
    reset(defaultFormValues);
    setShowSplitTransaction(false);
    clearErrors();
    setIsEditTransaction("");
    // Clear refs when clearing form
    customerRefs.current = {};
  };

  // Handle modal close with cleanup
  const handleModalClose = (open: boolean) => {
    if (!open) {
      clearAllFormData();
    }
    onOpenChange(open);
  };

  // Validate individual split row fields
  const validateSplitField = (
    idx: number,
    fieldName: keyof SplitRow,
    value: any
  ): boolean => {
    const fieldPath = `splitRows.${idx}.${fieldName}` as const;

    let isValid = false;

    switch (fieldName) {
      case "customerName":
      case "category":
        isValid = value !== null && value !== undefined && value?.value;
        break;
      case "amount":
        isValid = value && value.trim() !== "" && !isNaN(parseFloat(value));
        break;
      case "description":
        isValid = value && value.trim() !== "";
        break;
      default:
        isValid = false;
    }

    if (!isValid) {
      const fieldLabels = {
        customerName: "Customer/Vendor",
        category: "Category",
        amount: "Amount",
        description: "Description",
      };
      setError(fieldPath, {
        type: "manual",
        message: `${fieldLabels[fieldName]} is required`,
      });
      return false;
    } else {
      clearErrors(fieldPath);
      return true;
    }
  };

  // Handle amount change with validation
  const handleAmountChange = (idx: number, value: string) => {
    // Clean the input to allow only numbers and decimal point
    const newValue = value.replace(/[^0-9.]/g, "").replace(/\.(?=.*\.)/g, "");

    setValue(`splitRows.${idx}.amount`, newValue);

    // Validate required field
    if (!newValue || newValue.trim() === "" || isNaN(parseFloat(newValue))) {
      setError(`splitRows.${idx}.amount`, {
        type: "manual",
        message: "Valid amount is required",
      });
      return;
    }

    // Check if sum exceeds original amount
    const tempRows = [...(splitRows || [])];
    if (tempRows[idx]) {
      tempRows[idx].amount = newValue;

      const tempSum = tempRows.reduce(
        (sum, row) => sum + (parseFloat(row.amount) || 0),
        0
      );

      if (tempSum > Math.abs(originalAmount)) {
        setError(`splitRows.${idx}.amount`, {
          type: "manual",
          message: `Total split amount cannot exceed ${formatUniversalNumber(
            originalAmount
          )}`,
        });
      } else {
        clearErrors(`splitRows.${idx}.amount`);
      }
    }
  };

  // Handle split row field changes with validation
  const handleSplitFieldChange = (
    idx: number,
    fieldName: keyof SplitRow,
    value: any
  ) => {
    setValue(`splitRows.${idx}.${fieldName}`, value);

    // Validate after a short delay to ensure setValue has processed
    setTimeout(() => {
      validateSplitField(idx, fieldName, value);
    }, 0);
  };

  const handleAddSplit = () => {
    // Validate current rows before adding new one
    let hasError = false;
    const currentRows = getValues("splitRows");

    if (currentRows && currentRows.length > 0) {
      const lastRowIndex = currentRows.length - 1;
      const lastRow = currentRows[lastRowIndex];

      if (lastRow) {
        Object.keys(lastRow).forEach((key) => {
          const fieldKey = key as keyof SplitRow;
          if (!validateSplitField(lastRowIndex, fieldKey, lastRow[fieldKey])) {
            hasError = true;
          }
        });
      }
    }

    if (hasError) return;

    // Check if adding would exceed original amount
    if (splitAmount >= Math.abs(originalAmount)) {
      return;
    }

    // Get the current number of rows to determine the new row index
    const newRowIndex = fields.length;

    append(createDefaultSplitRow());

    // Focus on the new row's customer field after it's added
    setTimeout(() => {
      if (customerRefs.current[newRowIndex]) {
        customerRefs.current[newRowIndex].focus?.();
      }
    }, 0);
  };

  const handleResetSplitRows = () => {
    replace([createDefaultSplitRow()]);
    clearErrors();
    // Clear refs and focus on first row
    customerRefs.current = {};
    setTimeout(() => {
      if (customerRefs.current[0]) {
        customerRefs.current[0].focus?.();
      }
    }, 0);
  };

  // Validate all split rows
  const validateAllSplitRows = (): boolean => {
    if (!showSplitTransaction) return true;

    const currentRows = getValues("splitRows");
    if (!currentRows || currentRows.length === 0) {
      setError("splitRows", {
        type: "manual",
        message: "At least one split row is required",
      });
      return false;
    }

    let isValid = true;
    currentRows.forEach((row, idx) => {
      Object.keys(row).forEach((key) => {
        const fieldKey = key as keyof SplitRow;
        if (!validateSplitField(idx, fieldKey, row[fieldKey])) {
          isValid = false;
        }
      });
    });

    return isValid;
  };

  function buildTransactionPayload(
    data: EditTransactionFormValues,
    editTransaction: any,
    isApply: boolean
  ): ApplyAndConfirmTransactionDataPayload {
    const splitTransactionDetails = showSplitTransaction
      ? data?.splitRows?.map((item: SplitRow) => {
          return {
            customerId:
              item?.customerName?.other === "C"
                ? item?.customerName?.value
                : null,
            vendorId:
              item?.customerName?.other === "V"
                ? item?.customerName?.value
                : null,
            amount: Number(item?.amount),
            description: item?.description || "",
            categoryId: item?.category?.value || "",
          };
        })
      : [
          {
            customerId:
              data?.customerName?.other === "C"
                ? data?.customerName?.value
                : null,
            vendorId:
              data?.customerName?.other === "V"
                ? data?.customerName?.value
                : null,
            amount: Number(editTransaction?.amount),
            description: "",
            categoryId: data?.category?.value || "",
          },
        ];

    return {
      transactionId: editTransaction?.transactionId || "",
      typeOfTransaction: data.transactionType?.value || "",
      remarks: data.remark || "",
      transactionAmount: Math.abs(editTransaction?.amount),
      splitTransacionDetails: splitTransactionDetails,
    };
  }

  // Build JSON on submit
  const onSubmit = async (data: EditTransactionFormValues) => {
    // Validate split transaction total
    if (showSplitTransaction) {
      if (!validateAllSplitRows()) {
        return;
      }

      const tempSum =
        data.splitRows?.reduce(
          (sum, row) => sum + (parseFloat(row.amount) || 0),
          0
        ) || 0;

      if (tempSum > Math.abs(originalAmount)) {
        setError(`splitRows.${data.splitRows.length - 1}.amount`, {
          type: "manual",
          message: `Total split amount cannot exceed ${formatUniversalNumber(
            originalAmount
          )}`,
        });
        return;
      }

      if (tempSum !== Math.abs(originalAmount)) {
        // Show error for difference
        setError("splitRows", {
          type: "manual",
          message: `Split amount must equal original amount of ${formatUniversalNumber(
            originalAmount
          )}`,
        });
        return;
      }
    }

    const result = buildTransactionPayload(data, editTransaction, false);
    if (!handleApply) return;
    await handleApply("confirm", result);
  };

  return (
    <Dialog open={open} onOpenChange={handleModalClose}>
      <DialogContent
        aria-describedby="edit-transaction-desc"
        className="w-[1000px] max-w-[90vw] overflow-y-auto"
      >
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <DialogTitle className="BookEZ_font_color_gray_2 BookEZ_font_sub_heading">
            Select Category
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-6">
            {/* Transaction Details */}
            <Card className="bg-[#F4F4F4]">
              <CardContent className="p-4">
                <div className="grid lg:grid-cols-4! md:grid-cols-2! grid-cols-1! gap-4 text-sm">
                  <div>
                    <div className="font-medium BookEZ_font_header_color">
                      Date
                    </div>
                    <div className="BookEZ_font_color_gray_3">
                      {editTransaction?.transactionDate
                        ? formatToShortDate(editTransaction?.transactionDate)
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <div className="font-medium BookEZ_font_header_color">
                      Transaction
                    </div>
                    <div
                      className="BookEZ_font_color_gray_3 truncate overflow-hidden whitespace-nowrap"
                      title={editTransaction?.personalFinanceCategoryDetailed}
                    >
                      {editTransaction?.personalFinanceCategoryDetailed}
                    </div>
                  </div>
                  <div>
                    <div className="font-medium BookEZ_font_header_color">
                      Account
                    </div>
                    <div className="BookEZ_font_color_gray_3">
                      {editTransaction?.merchantName}
                    </div>
                  </div>
                  <div>
                    <div className="font-medium BookEZ_font_header_color">
                      Amount
                    </div>
                    <div className="BookEZ_font_color_gray_3">
                      {formatUniversalNumber(editTransaction?.amount)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <hr className="BookEZ_hr_bg_color" />

            {/* Form Fields */}
            <div className="grid md:grid-cols-3! grid-cols-1! gap-4">
              <div className="space-y-2">
                <Controller
                  name="transactionType"
                  control={control}
                  rules={{ required: "Transaction Type is required" }}
                  render={({ field }) => (
                    <CustomReactSelect
                      {...field}
                      id="transactionType"
                      name="transactionType"
                      options={transactionTypeOptions || []}
                      isClearable
                      label="Type of transactions *"
                      value={field.value}
                      onChange={(option) => field.onChange(option)}
                      placeholder="Select Transaction Type..."
                      className="min-w-auto"
                      classNamePrefix="BookEZ_custom_select"
                      error={errors.transactionType?.message as string}
                    />
                  )}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3! grid-cols-1! gap-4">
              <Controller
                name="customerName"
                control={control}
                rules={{
                  required: showSplitTransaction
                    ? false
                    : "Received from paid you is required",
                }}
                render={({ field }) => (
                  <CustomReactSelect
                    {...field}
                    id="customerName"
                    label="Vendor/who paid you"
                    name="customerName"
                    options={customerVendorOptions || []}
                    value={field.value}
                    onChange={(option) => field.onChange(option)}
                    placeholder="Select Vendor/who paid you..."
                    error={errors.customerName?.message as string}
                    isDisabled={showSplitTransaction}
                  />
                )}
              />
              <Controller
                name="category"
                control={control}
                rules={{
                  required: showSplitTransaction
                    ? false
                    : "Category is required",
                }}
                render={({ field }) => (
                  <CustomReactSelect
                    {...field}
                    id="category"
                    name="category"
                    options={categoryOptions || ([] as any)}
                    isClearable
                    label="Category *"
                    value={field.value}
                    isDisabled={showSplitTransaction}
                    onChange={(option) => field.onChange(option)}
                    placeholder="Select Category..."
                    className="min-w-auto"
                    classNamePrefix="BookEZ_custom_select"
                    labelAction={
                      <Button
                        className="ml-2 cursor-pointer text-sm"
                        onClick={handleSplit}
                        size="auto"
                        type="button"
                        role="button"
                        disabled={!watch("transactionType")?.value}
                        variant={"underline"}
                      >
                        Split transaction
                      </Button>
                    }
                    error={errors.category?.message as string}
                  />
                )}
              />
            </div>
            {showSplitTransaction && (
              <>
                <Card className="bg-[#F4F4F4] BookEZ_card_radius BookEZ_card_border_color">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="BookEZ_font_bold BookEZ_font_color_blue_dark BookEZ_font_body_size_large">
                        Split transaction
                      </h3>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 border-none!"
                        onClick={handleCloseSplit}
                        type="button"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Loading State */}
                    {isFetchingEditTransaction && (
                      <div className="flex justify-center items-center py-8 text-sm text-gray-500">
                        <Loader className="animate-spin mr-2" />
                        Loading split transaction data...
                      </div>
                    )}

                    {/* Split Content - Show when not loading */}
                    {!isFetchingEditTransaction && (
                      <div className="space-y-3">
                        {/* Error Alert - Show all split row errors at top */}
                        {(() => {
                          const allErrors: string[] = [];
                          fields.forEach((_, idx) => {
                            const rowErrors = [
                              errors?.splitRows?.[idx]?.customerName?.message,
                              errors?.splitRows?.[idx]?.category?.message,
                              errors?.splitRows?.[idx]?.amount?.message,
                              errors?.splitRows?.[idx]?.description?.message,
                            ].filter(Boolean) as string[];

                            if (rowErrors.length > 0) {
                              allErrors.push(
                                `Row ${idx + 1}: ${rowErrors.join(", ")}`
                              );
                            }
                          });

                          // Add general split errors
                          if (errors.splitRows?.message) {
                            allErrors.push(errors.splitRows.message);
                          }

                          return allErrors.length > 0 ? (
                            <div className="BookEZ_error_transparent border BookEZ_error_border rounded-md p-3 text-xs bg-red-50 border-red-200">
                              <div className="font-medium text-red-800 mb-1">
                                Please fix the following errors:
                              </div>
                              {allErrors.map((error, index) => (
                                <div key={index} className="text-red-700">
                                  • {error}
                                </div>
                              ))}
                            </div>
                          ) : null;
                        })()}

                        {/* Split Rows */}
                        {fields.map((row: any, idx: number) => (
                          <div
                            key={row.id}
                            className="grid grid-cols-12 gap-2 items-center"
                          >
                            <div className="lg:col-span-3! col-span-6!">
                              <Controller
                                name={`splitRows.${idx}.customerName`}
                                control={control}
                                render={({ field }) => (
                                  <CustomReactSelect
                                    {...field}
                                    ref={(ref) => {
                                      // Store ref for this specific row index
                                      customerRefs.current[idx] = ref;
                                    }}
                                    label={idx === 0 ? "Received from" : ""}
                                    id={`splitRows.${idx}.customerName`}
                                    name={`splitRows.${idx}.customerName`}
                                    className="min-w-auto"
                                    options={customerVendorOptions || []}
                                    onChange={(option) => {
                                      field.onChange(option);
                                      handleSplitFieldChange(
                                        idx,
                                        "customerName",
                                        option
                                      );
                                    }}
                                    placeholder="Received from"
                                    value={field.value}
                                    isClearable
                                  />
                                )}
                              />
                            </div>
                            <div className="lg:col-span-3! col-span-6!">
                              <Controller
                                name={`splitRows.${idx}.category`}
                                control={control}
                                render={({ field }) => (
                                  <CustomReactSelect
                                    {...field}
                                    label={idx === 0 ? "Category" : ""}
                                    id={`splitRows.${idx}.category`}
                                    name={`splitRows.${idx}.category`}
                                    className="min-w-auto"
                                    value={field.value}
                                    options={categoryOptions || ([] as any)}
                                    onChange={(option) => {
                                      field.onChange(option);
                                      handleSplitFieldChange(
                                        idx,
                                        "category",
                                        option
                                      );
                                    }}
                                    placeholder="Select Category..."
                                    isClearable
                                  />
                                )}
                              />
                            </div>
                            <div className="lg:col-span-3! col-span-6!">
                              <Controller
                                name={`splitRows.${idx}.description`}
                                control={control}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    id={`splitRows.${idx}.description`}
                                    className=""
                                    label={idx === 0 ? "Description" : ""}
                                    placeholder=""
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                      handleSplitFieldChange(
                                        idx,
                                        "description",
                                        e.target.value
                                      );
                                    }}
                                  />
                                )}
                              />
                            </div>
                            <div className="lg:col-span-2! col-span-4!">
                              <Controller
                                name={`splitRows.${idx}.amount`}
                                control={control}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    id={`splitRows.${idx}.amount`}
                                    name={`splitRows.${idx}.amount`}
                                    value={field.value}
                                    type="text"
                                    label={idx === 0 ? "Amount" : undefined}
                                    placeholder=""
                                    onChange={(e) =>
                                      handleAmountChange(idx, e.target.value)
                                    }
                                  />
                                )}
                              />
                            </div>
                            <div
                              className={`md:col-span-1! col-span-2! self-end`}
                            >
                              <Button
                                variant="ghost"
                                size="icon"
                                className="w-8 border-none!"
                                disabled={fields.length === 1}
                                onClick={() => remove(idx)}
                                type="button"
                              >
                                <Delete />
                                {/* <Trash2 className="h-4 w-4" color="#1E1E1E" /> */}
                              </Button>
                            </div>
                          </div>
                        ))}

                        {/* Add Split and Reset buttons */}
                        <div className="flex gap-4">
                          <Button
                            type="button"
                            variant="ghost"
                            className="text-blue-600! border-none hover:underline hover:underline-offset-2 px-0 h-auto"
                            onClick={handleAddSplit}
                            disabled={splitAmount >= Math.abs(originalAmount)}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Split
                          </Button>
                          <Button
                            variant="ghost"
                            className="px-0 h-auto hover:underline hover:underline-offset-2 border-none"
                            type="button"
                            onClick={handleResetSplitRows}
                          >
                            Reset
                          </Button>
                        </div>
                      </div>
                    )}

                    <hr className="BookEZ_hr_bg_color my-2" />

                    {/* Note and Attachments */}
                    <div className="BookEZ_font_color_gray_2">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Note Section */}
                        <div className="">
                          <Label htmlFor="note" className="">
                            Note
                          </Label>
                          <Controller
                            name="remark"
                            control={control}
                            render={({ field }) => (
                              <Textarea
                                {...field}
                                id="note"
                                className="min-h-[80px] resize-none"
                                placeholder="Add a note..."
                              />
                            )}
                          />
                        </div>
                        <div></div>

                        {/* Split Amount Summary */}
                        <div className="space-y-3 BookEZ_font_color_gray_3 justify-end pr-4">
                          <div className="flex justify-between items-center">
                            <span className="">Split Amount</span>
                            <span className="">
                              {formatUniversalNumber(splitAmount)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="">Original Amount</span>
                            <span className="">
                              {formatUniversalNumber(originalAmount)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span>Difference</span>
                            <span
                              className={
                                difference < 0 ? "BookEZ_error_text" : ""
                              }
                            >
                              {formatUniversalNumber(difference)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-between items-center gap-3 pt-4">
              <Button
                variant="underlineWithoutBorder"
                onClick={() => handleModalClose(false)}
                type="button"
              >
                Cancel
              </Button>
              <div className="flex flex-wrap gap-3 items-center">
                <Button
                  variant="outline"
                  type="button"
                  onClick={handleSubmit((data: EditTransactionFormValues) => {
                    if (selectedRows.length > 0) {
                      const passData = buildTransactionPayload(
                        data,
                        editTransaction,
                        true
                      );
                      if (passData) {
                        handleApply && handleApply("apply", passData);
                        clearAllFormData();
                      }
                    }
                  })}
                  loading={loading?.isApplyingTransaction}
                  disabled={
                    loading?.isApplyingTransaction ||
                    (difference !== 0 && showSplitTransaction)
                  }
                >
                  {loading?.isApplyingTransaction ? "Applying..." : "Apply"}
                </Button>
                <Button
                  variant="filled"
                  type="submit"
                  disabled={
                    isConfirming || (difference !== 0 && showSplitTransaction)
                  }
                  loading={isConfirming}
                >
                  Apply &amp; Confirm
                </Button>
              </div>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
